import React, { useState } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@mui/system";

/**
 * BrandGlow — the soft light behind the sign-in form and the Home pages, in the two
 * colours of the Fusion MSI star, blue and gold, from opposite corners (which ones
 * depends on the page; see PRESETS). Each corner's light is a loose cloud of three
 * blurred patches of the same colour, each drifting on its own slow clock, so the
 * shape is never a clean circle or ellipse and keeps changing.
 *
 * It fills its parent, which must be positioned and clip its overflow (AuthPageShell
 * does; on the dashboard use BrandGlowLayer). Purely decorative, behind content: the
 * parent should set `isolation: "isolate"` so the negative z-index stays inside it.
 *
 * The drift runs on the wall clock (a negative delay from Date.now()), not from
 * mount: each sign-in screen (AuthPage, LinkDevicePage) mounts its own glow, and
 * stepping between them should continue the motion rather than restart it.
 */

// Where a light sits, and which way is into the page from there.
const CORNERS = {
  topLeft: { x: "0%", y: "0%", inward: { x: 1, y: 1 } },
  topRight: { x: "100%", y: "0%", inward: { x: -1, y: 1 } },
  bottomLeft: { x: "0%", y: "100%", inward: { x: 1, y: -1 } },
  bottomRight: { x: "100%", y: "100%", inward: { x: -1, y: -1 } },
};

// Size is the largest patch's width; alphas are [light mode, dark mode]. The two
// lights take opposite corners, chosen to stay off what sits in a corner: sign-in
// has its logo top-left, and the dashboard has the avatar top-right.
const PRESETS = {
  // Sign-in: big, since there is only a small form on the page.
  auth: {
    size: "min(2000px, 210vw)",
    alpha: [0.2, 0.34],
    blue: CORNERS.bottomLeft,
    gold: CORNERS.topRight,
  },
  // Home: smaller and dimmer, since cards sit on top.
  home: {
    size: "min(1400px, 145vw)",
    alpha: [0.15, 0.27],
    blue: CORNERS.topLeft,
    gold: CORNERS.bottomRight,
  },
};

// ⚠️ THE LIGHTS NEVER OVERLAP MUCH. Blue and gold mix to a muddy green wherever
// they do, so they live in opposite corners and every patch stays near its own
// corner: the inward offsets below are small next to the page's diagonal.
//
// Each patch: scale of the preset size, aspect ratio, resting angle, how far its
// centre sits in from the corner (as a share of the size; negative is off the
// screen), and its loop length. Every centre is off the screen, so only the light's
// falloff shows, never its hot middle.
// The loop lengths share no common factor, so the cloud never repeats a shape.
//
// Wide and low: the patches lie close to horizontal, so the light spreads along the
// top and bottom edges rather than climbing the sides. With one light along the
// top and the other along the bottom, flatter also means further apart.
const PATCHES = [
  { scale: 1, aspect: 2.8, angle: 6, inset: -0.05, ms: 17000 },
  { scale: 0.7, aspect: 2.2, angle: -8, inset: -0.02, ms: 23000 },
  { scale: 0.5, aspect: 3.2, angle: 2, inset: -0.03, ms: 29000 },
];

// Mostly sideways drift, so the light seems to slide along its edge.
const drift = (angle) => keyframes`
  0%, 100% { transform: translate(-50%, -50%) rotate(${angle}deg) scale(1); }
  30% { transform: translate(-42%, -53%) rotate(${angle + 4}deg) scale(1.08); }
  65% { transform: translate(-57%, -48%) rotate(${angle - 3}deg) scale(0.94); }
`;

const BrandGlow = ({ preset = "auth" }) => {
  const { size, alpha: alphas, blue, gold } = PRESETS[preset] || PRESETS.auth;
  // Read once per mount: where the shared clock is in each patch's cycle right now.
  const [now] = useState(() => Date.now());

  const corner = ({ x, y, inward, colour, strengthFor }) =>
    PATCHES.map((patch) => (
      <Box
        key={`${x}-${patch.ms}`}
        sx={({ palette }) => ({
          position: "absolute",
          // Nudged in from the corner along the diagonal, toward the page.
          left: `calc(${x} + ${inward.x} * ${patch.inset} * ${size})`,
          top: `calc(${y} + ${inward.y} * ${patch.inset} * ${size})`,
          width: `calc(${size} * ${patch.scale})`,
          aspectRatio: String(patch.aspect),
          transform: `translate(-50%, -50%) rotate(${patch.angle}deg)`,
          // Overlapping patches add up, so each carries a share of the strength.
          background: `radial-gradient(closest-side, ${alpha(colour(palette), strengthFor(palette) * 0.55)}, transparent)`,
          filter: "blur(24px)",
          animation: `${drift(patch.angle)} ${patch.ms}ms ease-in-out ${-(now % patch.ms)}ms infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        })}
      />
    ));

  const baseStrength = (palette) => alphas[palette.mode === "dark" ? 1 : 0];

  return (
    <Box
      aria-hidden
      sx={{ position: "absolute", inset: 0, zIndex: -1, pointerEvents: "none" }}
    >
      {corner({
        ...blue,
        // palette.brand comes from the app theme (theme.js); a bare MUI theme (tests,
        // Storybook) falls back to secondary.
        colour: (palette) => palette.brand?.blue || palette.secondary.main,
        strengthFor: baseStrength,
      })}
      {corner({
        ...gold,
        colour: (palette) => palette.brand?.gold || palette.warning.main,
        // Gold reads louder than blue on a dark page, and barely shows on white.
        strengthFor: (palette) =>
          baseStrength(palette) * (palette.mode === "dark" ? 0.75 : 1.2),
      })}
    </Box>
  );
};

/**
 * BrandGlow clipped to the nearest positioned ancestor (on the dashboard, the
 * scrolling <main>), so its corners are the content area's corners and it adds no
 * scrollbar. Its parent must not be positioned itself, and should set
 * `isolation: "isolate"` to keep the glow behind its content.
 */
export const BrandGlowLayer = (props) => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      zIndex: -1,
      pointerEvents: "none",
      isolation: "isolate",
    }}
  >
    <BrandGlow {...props} />
  </Box>
);

export default BrandGlow;
